/**
 * What the platform records as having happened, and to what.
 *
 * The vocabulary lives here rather than in the kernel that holds the table,
 * because the kernel must not learn what a book is: it stores a row and knows
 * the shape of one, while the words come from the contexts that write them.
 * Shared is where a word both sides need is written down once — the schema
 * builds its check from these lists and the service validates against them, so
 * there is no second spelling to keep in step.
 *
 * **Both lists are closed, and the database checks them.** Free-text verbs
 * become forty spellings of "updated", and a subject kind invented at a call
 * site is one no reader will ever look for. Adding either is a migration, which
 * is the price of them staying answerable in a query.
 *
 * A verb names what somebody DID, in the past tense, and never how it should
 * read: a rendered sentence in a row cannot be re-worded, cannot be translated,
 * and would freeze the Hebrew-beside-English pairing the studio sets its text
 * by. The line is composed where it is shown.
 */

/**
 * `doc` is a document of any host, named by its doc row, the platform's one
 * identity for a document: what is followed and read is the document, which
 * is the same thing for a book, a page and a deck. What was done to it is the
 * verb's to say.
 *
 * `space` is a space's own trail: who it let in or shut out, what it published,
 * what came and left. Its subject is the space rather than the document because
 * a document's history follows it when it changes hands, and a space's account
 * of what it did must not leave with it.
 */
export const ACTIVITY_SUBJECT_KINDS = [
  'doc',
  'donation',
  'pledge',
  'payment',
  'arrangement',
  'space',
] as const
export type ActivitySubjectKind = (typeof ACTIVITY_SUBJECT_KINDS)[number]

/**
 * A book, and the things done to one.
 *
 * A comment is activity ON a book rather than a subject of its own: what a
 * person follows is the book, so a thread that named itself as the subject
 * would be a second thing to follow for no gain. Which comment it was is in the
 * payload.
 *
 * **A part is REMOVED and a book is DELETED, and the two words are not
 * interchangeable here.** A removed section, block or note is tombstoned and
 * stays for ever; a deleted book is tombstoned and then actually deleted when
 * its grace period runs out. One verb over both would make the feed unable to
 * say which of those a line is about.
 */
export const BOOK_VERBS = [
  'book_edited',
  'book_deleted',
  'book_restored',
  'section_added',
  'section_edited',
  'section_removed',
  'section_restored',
  'section_moved',
  'block_added',
  'block_edited',
  'block_removed',
  'block_restored',
  'block_moved',
  'version_named',
  'version_restored',
  'words_replaced',
  'note_added',
  'note_edited',
  'note_removed',
  'note_restored',
  'comment_posted',
  'comment_edited',
  'comment_removed',
  'comment_resolved',
  'comment_reopened',
] as const
export type BookVerb = (typeof BOOK_VERBS)[number]

/**
 * A gift, and what the office corrects about one.
 *
 * Two verbs for two acts. `donation_repointed` is the narrow one — only what a
 * gift COUNTS TOWARD, the item on the shul's list and the campaign year.
 * `donation_corrected` is the office editing the record itself: any of the
 * money facts, the words either author wrote, and which billing account it
 * belongs to.
 *
 * Both carry the PREVIOUS value of everything they changed, because that value
 * exists nowhere else once it is overwritten. The row stamps `attributed_by`
 * and `attributed_at` for the billing account, and the line still names the
 * billing account it moved FROM: the stamp says who ruled and when, never what
 * the ruling replaced.
 */
export const DONATION_VERBS = [
  'donation_repointed',
  'donation_corrected',
  'donation_applied',
  'donation_deleted',
] as const
export type DonationVerb = (typeof DONATION_VERBS)[number]

/**
 * A pledge somebody made, and what the office corrects about one.
 *
 * `pledge_corrected` is a pledge still waiting to be answered — its billing
 * account, its dedication, its figure, the plan it proposes. `pledge_moved` is
 * one already confirmed, where the record is a charge with money settled on it
 * and a plan running: moving that to another billing account carries both, so
 * the line names what went with it.
 *
 * The subject is the PLEDGE in both cases, including once it is a charge. What
 * a reader is following is the promise somebody made, and splitting its history
 * at the moment it was confirmed would leave each half unreadable on its own.
 */
export const PLEDGE_VERBS = ['pledge_corrected', 'pledge_moved'] as const
export type PledgeVerb = (typeof PLEDGE_VERBS)[number]

/** A hand-recorded payment, and the facts the office corrected on it. */
export const PAYMENT_VERBS = ['payment_corrected'] as const
export type PaymentVerb = (typeof PAYMENT_VERBS)[number]

/**
 * Prepaid periods that were recorded one charge per period, merged into one
 * stretch. The payload snapshots the run, because the retired charge rows
 * are gone.
 */
export const ARRANGEMENT_VERBS = ['arrangement_periods_merged'] as const
export type ArrangementVerb = (typeof ARRANGEMENT_VERBS)[number]

/**
 * What a Studio space's trail records: who was let in or shut out, which
 * public links were made or withdrawn, and which documents came, left or were
 * handed over. Each line names the record and the people as they read then,
 * so it still reads after the document has left the space.
 */
export const TRAIL_VERBS = [
  'access_granted',
  'access_changed',
  'access_ended',
  'link_made',
  'link_changed',
  'link_withdrawn',
  'moved_in',
  'moved_out',
  'handover_offered',
  'handover_withdrawn',
  'handover_declined',
  'handover_accepted',
] as const
export type TrailVerb = (typeof TRAIL_VERBS)[number]

/**
 * Every verb the log can hold, across the contexts that write it.
 *
 * Assembled from the per-subject lists rather than typed out again, so the
 * database's check and each context's own vocabulary cannot come apart. A
 * context's tables — how its lines read, how they collapse — key on its OWN
 * list, which is what keeps the studio from owing a sentence about money.
 */
export const ACTIVITY_VERBS = [
  ...BOOK_VERBS,
  ...DONATION_VERBS,
  ...PLEDGE_VERBS,
  ...PAYMENT_VERBS,
  ...ARRANGEMENT_VERBS,
  ...TRAIL_VERBS,
] as const
export type ActivityVerb = (typeof ACTIVITY_VERBS)[number]

/**
 * The verb-specific detail, and the limit on what may go in it.
 *
 * Current relationships belong in columns with real keys. A verb may instead
 * snapshot values and identifiers that an act removes, so its history still
 * reads after the source row is gone.
 */
export type DonationAppliedAllocation = {
  chargeId: string
  amountCents: number
}

/** The gift snapshot that survives its conversion into a receivable payment. */
export type DonationAppliedPayload = {
  originalDonationId: string
  amountCents: number
  surchargeCents: number
  currency: string
  receivedAt: string
  donorName: string | null
  donorEmail: string | null
  description: string | null
  note: string | null
  /** The account the gift was on, which the payment it became is on. */
  billingAccountId: string
  personId: string | null
  chargeItemId: string | null
  campaignId: string | null
  unlisted: boolean
  attributedBy: string | null
  attributedAt: string | null
  acknowledgedAt: string | null
  stripeCheckoutId: string | null
  stripePaymentIntentId: string | null
  paymentId: string
  allocations: DonationAppliedAllocation[]
}

/** The gift snapshot that survives the row being deleted. */
export type DonationDeletedPayload = {
  originalDonationId: string
  /** The account the gift was on; null for a gift on no account. */
  billingAccountId: string | null
  personId: string | null
  campaignId: string | null
  chargeItemId: string | null
  description: string | null
  unlisted: boolean
  incomeAccountId: string
  amountCents: number
  surchargeCents: number
  stripeFeeCents: number | null
  disputedCents: number
  disputeStatus: string | null
  currency: string
  receivedAt: string
  source: string
  donorName: string | null
  donorEmail: string | null
  stripeCheckoutId: string | null
  checkoutLine: number
  stripePaymentIntentId: string | null
  sourcePaymentId: string | null
  method: string | null
  reference: string | null
  note: string | null
  recordedBy: string | null
  attributedBy: string | null
  attributedAt: string | null
  periodKey: string | null
  acknowledgedAt: string | null
  createdAt: string
}

/** One period charge as it stood before a prepaid stretch merge retired it. */
export type ArrangementPeriodMergedCharge = {
  id: string
  periodKey: string | null
  periodCount: number
  amountCents: number
  paidCents: number
  status: string
  issuedAt: string
}

/** One settlement on those charges, as it stood before the merge moved it. */
export type ArrangementPeriodMergedSettlement = {
  chargeId: string
  paymentId: string | null
  creditId: string | null
  cents: number
}

/** What a prepaid stretch merge recorded, after the extra charge rows are gone. */
export type ArrangementPeriodsMergedPayload = {
  periodKeys: string[]
  count: number
  amountCents: number
  issuedAt: string
  paymentId: string
  keptChargeId: string
  removedChargeIds: string[]
  charges: ArrangementPeriodMergedCharge[]
  settlements: ArrangementPeriodMergedSettlement[]
}

export type ActivityPayloadValue =
  | string
  | number
  | boolean
  | null
  | ActivityPayloadValue[]
  | { [key: string]: ActivityPayloadValue }

export type ActivityPayload = Record<string, ActivityPayloadValue>

/**
 * How repeated activity collapses into one line.
 *
 * Somebody working through a chapter writes twenty lines of log in an hour, and
 * twenty lines is not an account of what happened — "Rivka edited 20 blocks" is.
 * So a feed groups, and this is the rule it groups by, held as data because it
 * differs per verb: editing goes in bursts and collapses over an hour, while a
 * comment is a thing somebody said and is worth its own line for longer.
 *
 * `minCount` is the number below which a group is not worth making. At 2, one
 * edit reads as one edit rather than as "1 edit by 1 person", which is the
 * failure mode of collapsing unconditionally.
 *
 * Read by the band and by the rollup from here, so the two cannot come to
 * different accounts of one hour — the same reason a time is computed once.
 */
export type ActivityGrouping = {
  /** How long a group stays open, in minutes. */
  window: number
  /** Below this many, the lines stand on their own. */
  minCount: number
}

const HOUR = 60

/** One grouping for every verb of a list, keyed by exactly those verbs. */
function eachOf<V extends string>(
  verbs: readonly V[],
  grouping: ActivityGrouping,
): Record<V, ActivityGrouping> {
  // `fromEntries` widens its keys to string; the entries are exactly `verbs`.
  return Object.fromEntries(verbs.map((v) => [v, grouping])) as Record<
    V,
    ActivityGrouping
  >
}

export const ACTIVITY_GROUPING: Record<ActivityVerb, ActivityGrouping> = {
  book_edited: { window: HOUR, minCount: 2 },
  /* Deleting a whole book and putting it back are single, deliberate acts on
     the thing the feed is ABOUT, so neither ever folds into a count — "2 books
     deleted" in the history of one book says nothing at all. */
  book_deleted: { window: HOUR, minCount: 99 },
  book_restored: { window: HOUR, minCount: 99 },
  section_added: { window: HOUR, minCount: 2 },
  section_edited: { window: HOUR, minCount: 2 },
  section_removed: { window: HOUR, minCount: 2 },
  section_restored: { window: HOUR, minCount: 2 },
  section_moved: { window: HOUR, minCount: 3 },
  block_added: { window: HOUR, minCount: 2 },
  block_edited: { window: HOUR, minCount: 2 },
  block_removed: { window: HOUR, minCount: 2 },
  block_restored: { window: HOUR, minCount: 2 },
  block_moved: { window: HOUR, minCount: 3 },
  /* Naming a moment is deliberate and rare, and the NAME is the whole of what
     it says — so two of them never fold into "2 versions". */
  version_named: { window: HOUR, minCount: 99 },
  version_restored: { window: HOUR, minCount: 99 },
  /* A replace never folds into a count: it is one deliberate act reaching a
     whole book at once, so how much it moved and how many passages it reached
     is the line. Folded into "2 replacements" a reader could not tell which of
     them moved the words they are looking at. It carries counts and never the
     phrase — this is the act somebody takes a name out of a book with, and a
     log that kept the name would outlive the passage it came from. */
  words_replaced: { window: HOUR, minCount: 99 },
  note_added: { window: HOUR, minCount: 2 },
  note_edited: { window: HOUR, minCount: 2 },
  note_removed: { window: HOUR, minCount: 2 },
  note_restored: { window: HOUR, minCount: 2 },
  /* What somebody SAID keeps its own line far longer than an edit: a remark is
     read, answered and settled, and folding two of them into "2 comments"
     throws away the only part anybody wanted. */
  comment_posted: { window: 4 * HOUR, minCount: 4 },
  comment_edited: { window: 4 * HOUR, minCount: 3 },
  comment_removed: { window: 4 * HOUR, minCount: 3 },
  comment_resolved: { window: 4 * HOUR, minCount: 3 },
  comment_reopened: { window: 4 * HOUR, minCount: 3 },
  /* A gift is re-pointed one at a time, deliberately, and the line's whole
     value is which thing it now counts toward — folded into "2 corrections"
     a reader could not tell which gift moved. */
  donation_repointed: { window: HOUR, minCount: 99 },
  /* A correction is one deliberate act on one record, and what it says is which
     fields moved and what they were. Folded into "2 corrections" the previous
     values — the whole reason the line exists — would be unreachable. */
  donation_corrected: { window: HOUR, minCount: 99 },
  /* Applying a gift removes its source row, so its snapshot always stands as
     one line and is never replaced by an aggregate that cannot restore it. */
  donation_applied: { window: HOUR, minCount: 99 },
  /* Deleting a gift removes its source row, so its recoverable snapshot always
     stands as one line. */
  donation_deleted: { window: HOUR, minCount: 99 },
  pledge_corrected: { window: HOUR, minCount: 99 },
  pledge_moved: { window: HOUR, minCount: 99 },
  payment_corrected: { window: HOUR, minCount: 99 },
  /* Merging prepaid period charges is one deliberate repair of one
     arrangement; the snapshot of what was merged always stands as one line. */
  arrangement_periods_merged: { window: HOUR, minCount: 99 },
  /* A trail is an audit: each line says who was let in, which link or which
     document, and "3 shares" would name none of them. */
  ...eachOf(TRAIL_VERBS, { window: HOUR, minCount: 99 }),
}

/**
 * Whether a verb changes the book a reader is holding.
 *
 * A reading is live: somebody with the book open is shown what it says now, so
 * something has to answer "has it moved since I composed this". This is that
 * answer, and it is a property of the VERB rather than a query somebody writes
 * per caller — asked in two places it would drift, and the direction it drifts
 * is silent, because a verb wrongly called quiet simply never reaches anybody.
 *
 * The test is what a reader would SEE differently, not what was written: a
 * comment is a layer beside the book with a read of its own, and naming a
 * moment says nothing about the words. Everything that composes — the tree, the
 * words, the notes hanging off them, the title the running head takes — is true.
 *
 * Exhaustive over the BOOK's verb list on purpose. A verb added without an
 * answer here is a compile error rather than a passage that quietly stops
 * updating. Verbs from another subject are absent because the question does not
 * reach them: nothing a shul's books record moves the words in a sefer.
 */
export const CHANGES_THE_BOOK: Record<BookVerb, boolean> = {
  book_edited: true,
  book_deleted: true,
  book_restored: true,
  section_added: true,
  section_edited: true,
  section_removed: true,
  section_restored: true,
  section_moved: true,
  block_added: true,
  block_edited: true,
  block_removed: true,
  block_restored: true,
  block_moved: true,
  /* A name on a moment, and the moment is not the book. Nothing a reader has
     on screen reads differently for it. */
  version_named: false,
  version_restored: true,
  words_replaced: true,
  note_added: true,
  note_edited: true,
  note_removed: true,
  note_restored: true,
  /* Remarks are their own layer with their own read, and they are not the
     words. A book whose comments moved is a book that says the same thing. */
  comment_posted: false,
  comment_edited: false,
  comment_removed: false,
  comment_resolved: false,
  comment_reopened: false,
}

/** The verbs that move what a reader sees, for a query to ask by. */
export const BOOK_CHANGING_VERBS = BOOK_VERBS.filter(
  (verb) => CHANGES_THE_BOOK[verb],
)

/** How many actors a collapsed line names before it says "and N others". */
export const ACTIVITY_ACTOR_SAMPLE = 3

/**
 * How far back a badge looks, and how high it counts.
 *
 * Both bound the MEANING before they bound the query. "412 unread" on a book
 * written over two years is a number nobody acts on; "30+ in the last month" is
 * one somebody reads and then opens the book. The feed itself still pages back
 * as far as the log goes — this is the badge's horizon, not the history's.
 *
 * Shared because the two ends have to agree: the count stops one past the cap,
 * and whatever draws it renders anything at the bound as a bound. Spelled at
 * each end, they would drift and the badge would state a number it had stopped
 * counting toward.
 */
export const UNREAD_WINDOW_DAYS = 30
export const UNREAD_CAP = 30

/**
 * How long a moment the system took is kept.
 *
 * **Seven days, and what it bounds is machine output rather than anybody's
 * work.** `checkpoint()` mints one before every run of writes on a changed
 * book, so on a book being authored through the tool surface these arrive at
 * agent rate — dozens in an afternoon, none of them a decision anybody made.
 * A week is long enough that "what did this look like before yesterday's run"
 * is answered from a moment rather than from a revision, and short enough that
 * the pile does not become the history.
 *
 * **It argues from its own evidence.** A moment a PERSON named has no time
 * limit at all, and the difference is the whole point: the window is not a
 * judgment about how long a version is interesting, it is the price of taking
 * one automatically. Where another window lands on a nearby figure that is a
 * coincidence to state, never a rule to maintain.
 *
 * **`studio_book_version.taken` is what tells the two apart**, written only
 * by `checkpoint` and never claimable over the wire. Sweeping on
 * `named_by_person_id IS NULL` instead would delete what people kept and keep
 * what the machine made — both paths carry the caller's actor, so those rows
 * are byte-identical in every column but this one.
 */
export const CHECKPOINT_KEPT_DAYS = 7
